import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaStore } from "react-icons/fa";

/**
 * Página de respuesta del checkout ePayco para compras de REDPLIKAPLACE.
 * El registro del pago lo hace el backend vía webhook de confirmación;
 * aquí solo se muestra el resultado al comprador.
 */
const PagRespuestaMarketplaceEpayco = () => {
    const [searchParams] = useSearchParams();
    const refPayco = searchParams.get('ref_payco');
    const listingId = searchParams.get('listingId');
    const [estado, setEstado] = useState(null); // 'Aceptada' | 'Rechazada' | 'Pendiente' | 'error'
    const [detalle, setDetalle] = useState(null);
    const consultado = useRef(false);

    useEffect(() => {
        if (!refPayco || consultado.current) return;
        consultado.current = true;
        fetch(`https://secure.epayco.co/validation/v1/reference/${refPayco}`)
            .then(res => res.json())
            .then(body => {
                const data = body?.data || {};
                setEstado(data.x_response || 'error');
                setDetalle({
                    descripcion: data.x_description || '',
                    monto: data.x_amount,
                    referencia: data.x_id_invoice || data.x_id_factura || refPayco,
                    correo: data.x_customer_email || '',
                });
            })
            .catch(() => setEstado('error'));
    }, [refPayco]);

    if (!refPayco) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
                <p className="text-slate-600 mb-4">No se encontró la referencia del pago.</p>
                <Link to="/" className="text-[#205087] font-semibold hover:text-[#1f94c2]">Ir al inicio</Link>
            </div>
        );
    }

    if (!estado) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
                <div className="animate-pulse text-[#1f94c2] mb-4"><FaStore className="w-12 h-12" /></div>
                <p className="text-slate-600">Consultando el estado de tu pago...</p>
            </div>
        );
    }

    const aprobada = estado === 'Aceptada';

    return (
        <div className="min-h-[60vh] flex items-center justify-center px-4 py-12 bg-gradient-to-b from-slate-50 to-white">
            <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center">
                {aprobada ? (
                    <FaCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                ) : (
                    <FaTimesCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                )}
                <h1 className="text-xl font-bold text-slate-800 mb-2">
                    {aprobada ? '¡Compra exitosa!' : `Transacción: ${estado}`}
                </h1>
                <p className="text-sm text-slate-600 mb-6">
                    {aprobada
                        ? 'Tu pago fue aprobado. Recibirás la información de tu compra en tu correo electrónico.'
                        : 'Tu pago no fue aprobado. Puedes intentarlo nuevamente desde la publicación.'}
                </p>

                {detalle && (
                    <div className="bg-slate-50 rounded-xl p-4 text-left text-sm space-y-1 mb-6">
                        {detalle.descripcion && (
                            <p><span className="font-semibold text-slate-700">Producto:</span> {detalle.descripcion}</p>
                        )}
                        {detalle.monto != null && (
                            <p><span className="font-semibold text-slate-700">Monto:</span> ${Number(detalle.monto).toLocaleString('es-CO')} COP</p>
                        )}
                        <p><span className="font-semibold text-slate-700">Referencia:</span> {detalle.referencia}</p>
                        {detalle.correo && (
                            <p><span className="font-semibold text-slate-700">Correo:</span> {detalle.correo}</p>
                        )}
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    {!aprobada && listingId && (
                        <Link
                            to={`/redplikaplace/producto/${listingId}`}
                            className="w-full py-2.5 rounded-xl font-semibold text-white bg-[#f79865] hover:bg-[#e88a55] transition-colors"
                        >
                            Volver a intentar
                        </Link>
                    )}
                    <Link
                        to="/redplikaplace"
                        className="w-full py-2.5 rounded-xl font-semibold text-[#205087] border-2 border-[#205087] hover:bg-blue-50 transition-colors"
                    >
                        Ir a REDPLIKAPLACE
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PagRespuestaMarketplaceEpayco;
